import type { ParentComponent } from "solid-js";
import { createSignal, For, Show } from "solid-js";
import { Title } from "@solidjs/meta";
import { router } from "~/inertia";
import { Toggle } from "~/common/toggle";
import { createPatientText, type PatientI18n, PatientTextProvider } from "~/patient_i18n/i18n";
import { PatientLanguageBar } from "~/patient_i18n/language_bar";

type PreferenceGroup = {
  key: string;
  label: string;
  description: string;
  enabled: boolean;
};

type Props = {
  patientFirstName?: string;
  emailConsent?: boolean;
  emailAddressSuppressed?: boolean;
  groups?: PreferenceGroup[];
  token?: string;
  saved?: boolean;
  error?: string;
  patientI18n: PatientI18n;
};

const PublicPageLayout: ParentComponent = (props) => <>{props.children}</>;

function EmailPreferencesShow(props: Props) {
  const [saving, setSaving] = createSignal(false);
  /* eslint-disable solid/reactivity */
  const [emailConsent, setEmailConsent] = createSignal(props.emailConsent ?? true);
  const [groups, setGroups] = createSignal<PreferenceGroup[]>(
    props.groups?.map((g) => ({ ...g })) ?? [],
  );
  // The language only changes on a full page load, so the strings are read once.
  const text = createPatientText(props.patientI18n);
  /* eslint-enable solid/reactivity */
  const t = text.t;

  function handleToggleGroup(key: string) {
    setGroups((prev) => prev.map((g) => (g.key === key ? { ...g, enabled: !g.enabled } : g)));
  }

  function handleToggleAll() {
    setEmailConsent((prev) => !prev);
  }

  function handleSave() {
    setSaving(true);
    router.patch(
      `/email-preferences/${props.token}`,
      {
        email_consent: emailConsent(),
        groups: groups().map((g) => ({ key: g.key, enabled: g.enabled })),
      },
      {
        preserveScroll: true,
        onFinish() {
          setSaving(false);
        },
      },
    );
  }

  return (
    <PatientTextProvider value={text}>
      <Title>{t("email_preferences.title")}</Title>

      <div class="mx-auto w-full max-w-xl px-4 py-12 sm:px-6">
        <PatientLanguageBar />
        <div class="overflow-hidden rounded-xl bg-white shadow-sm ring-1 ring-gray-200">
          <Show
            when={!props.error}
            fallback={
              <div class="p-8 text-center">
                <div class="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-50">
                  <svg
                    class="h-6 w-6 text-red-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke-width="1.5"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"
                    />
                  </svg>
                </div>
                <h2 class="text-lg font-semibold text-gray-900">{props.error}</h2>
                <p class="mt-2 text-sm text-gray-500">{t("email_preferences.use_recent_link")}</p>
              </div>
            }
          >
            <div class="border-b border-gray-100 px-8 py-6">
              <h1 class="text-xl font-semibold text-gray-900">{t("email_preferences.title")}</h1>
              <p class="mt-1 text-sm text-gray-500">
                {t("email_preferences.choose", { name: props.patientFirstName ?? "" })}
              </p>
            </div>

            <Show when={props.saved}>
              <div class="mx-8 mt-6 rounded-lg bg-green-50 px-4 py-3">
                <p class="text-sm font-medium text-green-800">{t("email_preferences.saved")}</p>
              </div>
            </Show>

            <Show when={props.emailAddressSuppressed}>
              <div class="mx-8 mt-6 rounded-lg bg-amber-50 px-4 py-3">
                <p class="text-sm font-medium text-amber-900">
                  {t("email_preferences.undeliverable_title")}
                </p>
                <p class="mt-1 text-sm text-amber-800">
                  {t("email_preferences.undeliverable_body")}
                </p>
              </div>
            </Show>

            <div class="px-8 py-6">
              <div class="flex items-center justify-between rounded-lg bg-gray-50 px-4 py-3">
                <div>
                  <p class="text-sm font-medium text-gray-900">
                    {t("email_preferences.receive_all")}
                  </p>
                  <p class="text-sm text-gray-500">
                    {t(emailConsent() ? "email_preferences.receiving" : "email_preferences.paused")}
                  </p>
                </div>
                <Toggle checked={emailConsent()} onChange={handleToggleAll} />
              </div>

              <Show when={emailConsent()}>
                <div class="mt-6">
                  <div class="divide-y divide-gray-100 rounded-lg border border-gray-200">
                    <For each={groups()}>
                      {(group) => (
                        <div class="flex items-center justify-between px-4 py-3">
                          <div class="pr-4">
                            <p class="text-sm font-medium text-gray-900">{group.label}</p>
                            <p class="text-sm text-gray-500">{group.description}</p>
                          </div>
                          <Toggle
                            checked={group.enabled}
                            onChange={() => handleToggleGroup(group.key)}
                          />
                        </div>
                      )}
                    </For>
                  </div>
                </div>
              </Show>
            </div>

            <div class="border-t border-gray-100 px-8 py-4">
              <button
                type="button"
                class="w-full rounded-lg bg-gray-900 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-50"
                onClick={handleSave}
                disabled={saving()}
              >
                {t(saving() ? "email_preferences.saving" : "email_preferences.save")}
              </button>
            </div>
          </Show>
        </div>
      </div>
    </PatientTextProvider>
  );
}

EmailPreferencesShow.layout = PublicPageLayout;
export default EmailPreferencesShow;
